import BuildConfig from '$extensionSrc/buildConfig'
import { XSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { TWITTER_AUTOMATION_STAGE_STATUSES } from './twitterAutomationStageStatuses'
import * as twitterUrls from './twitterUrls'
import { NO_TIMEOUT } from '../../consts/stageMachineConsts'
import { sendAlertNotification } from '../../utils/communication'
import {
  PLATFORM,
  StageType,
  TriggeringEventType,
  TwitterRecommendationKey,
} from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import { throwIfGlobalInterruptRequested } from '../globalInterrupt'
import { getAttrPromise, wait } from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import {
  getElement,
  getOptionalElement,
  getInitialValueBySettingKey,
} from '../utils'

export const getCurrentValueFn = () => () => ''
export const getIsFulfilledFn = () => () => true
export const getCantUndoFn = () => defaultCantUndoFn
export const getIsDisabledFn = () => defaultIsDisabledFn
export const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

export type XDeleteLikesState = {
  count: number // total count of posts deleted
  message: string
  result?: {
    title: string
    detail?: string
    helper?: {
      text: string
      link: string
    }
  }
  stats: {
    numLikesDeleted: number // in latest update
  }
}

async function waitForFirstLikedPostToLoad(): Promise<HTMLElement> {
  const timeline = await getElement<HTMLElement>(
    document,
    '//div[@aria-label="Home timeline"]',
  )

  const firstPost = await getOptionalElement<HTMLElement>(
    timeline,
    `./descendant::article[descendant::button[@data-testid="unlike"]]`,
  )

  return firstPost
}

async function getVisibleUnlikeButtons(): Promise<HTMLElement[]> {
  await waitForFirstLikedPostToLoad()
  const unlikeButtons = evaluateXpath<HTMLElement>(
    document,
    `//button[@data-testid="unlike"]`,
  )
  remoteLog.debug(
    `getVisibileUnlikeButtons: found ${unlikeButtons?.length} liked posts`,
  )

  return unlikeButtons || []
}

const StageKey = {
  UPDATE_GO_TO_PROFILE_LIKES: 'UPDATE_GO_TO_PROFILE_LIKES',
  UPDATE_DELETE_LIKES: 'UPDATE_DELETE_LIKES',
  UPDATE_DELETE_LIKES_FINAL: 'UPDATE_DELETE_LIKES_FINAL',
}

// No gather or undo stages
export const gatherStageDefs: StageDef[] = []
export const undoStageDefs: StageDef[] = []

export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_GO_TO_PROFILE_LIKES,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: twitterUrls.authHomeUrl,
    urlPattern: 'https://x.com/.+',
    transitionNextStageMap: {
      [TriggeringEventType.ON_ERROR]: StageKey.UPDATE_DELETE_LIKES_FINAL,
    },
    action: async (_machineContext, userContext) => {
      const { clearStoredAutomationErrors, updateStoredAutomationErrors } =
        userContext
      await clearStoredAutomationErrors(
        TwitterRecommendationKey.TWITTER_REMOVE_LIKES,
      )

      const profileUrl = await twitterUrls.getProfileUrl()
      if (!profileUrl) {
        const error = new Error(
          'Unable to find profile, failed to delete likes.',
        )
        await updateStoredAutomationErrors(
          {
            [XSettingKey.REMOVE_LIKES]: {
              gatherError: {
                message: error.message,
                name: error.name,
              },
            },
          },
          TwitterRecommendationKey.TWITTER_REMOVE_LIKES,
        )
        throw error
      } else {
        // Go to profile likes tab
        window.location.assign(`${profileUrl}/likes`)
      }
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_DELETE_LIKES,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.DELETING_LIKES,
    timeout: NO_TIMEOUT,
    action: async (_machineContext, userContext) => {
      const {
        incrementTotalCount,
        initialValues: storedInitialValues,
        isDry,
        updateStoredInitialValues,
      } = userContext

      let unlikeButtonsToClick = await getVisibleUnlikeButtons()

      if (unlikeButtonsToClick.length === 0) {
        sendAlertNotification(
          PLATFORM.TWITTER,
          'X: Delete all likes error',
          'No likes found on profile',
        )
      }
      let numLikesDeleted =
        getInitialValueBySettingKey(
          storedInitialValues,
          XSettingKey.REMOVE_LIKES,
        )?.count || 0
      let currentLikesDeleted = 0

      while (unlikeButtonsToClick.length > 0) {
        // Click all buttons
        for (const unlikeButton of unlikeButtonsToClick) {
          if (!isDry) {
            const updatePromise = Promise.race([
              getAttrPromise(unlikeButton, 'data-testid'),
              wait(3000),
            ])
            unlikeButton.click()

            await updatePromise
            if (updatePromise) {
              numLikesDeleted += 1
              currentLikesDeleted += 1

              await incrementTotalCount(
                TwitterRecommendationKey.TWITTER_REMOVE_LIKES,
              )
            }
            await wait(500)
          } else {
            numLikesDeleted += 1
            currentLikesDeleted += 1

            await incrementTotalCount(
              TwitterRecommendationKey.TWITTER_REMOVE_LIKES,
            )
          }

          await updateStoredInitialValues(
            {
              [XSettingKey.REMOVE_LIKES]: {
                count: numLikesDeleted,
                message: `You have removed ${numLikesDeleted} like${numLikesDeleted === 1 ? '' : 's'} `,
              },
            },
            TwitterRecommendationKey.TWITTER_REMOVE_LIKES,
          )

          await userContext.updateStatisticsLastCount(
            TwitterRecommendationKey.TWITTER_REMOVE_LIKES,
            currentLikesDeleted,
          )
        }

        // Scroll page
        window.scrollBy(0, window.innerHeight * 1.5)
        throwIfGlobalInterruptRequested()

        // Re-populate unlikeButtons array
        if (!isDry) {
          unlikeButtonsToClick = await getVisibleUnlikeButtons()
        } else {
          unlikeButtonsToClick = []
        }
      }

      sendAlertNotification(
        PLATFORM.TWITTER,
        'X: Finished deleting likes',
        `${BuildConfig.TENANT_NAME} has removed ${currentLikesDeleted} like${currentLikesDeleted === 1 ? '' : 's'} from your X profile`,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_DELETE_LIKES_FINAL,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.DELETING_LIKES,
    action: async () => {
      // Refresh page so previously liked posts disappear
      window.location.reload()
    },
  },
]
